/**
 * DeviceIdentityService
 * Manages a stable IdUnique (SHA-256 of UUID+fingerprint+instanceId) with triple persistence:
 *   1. localStorage  — fastest read
 *   2. Dexie `settings` table — survives localStorage clear
 *   3. Cookie        — survives localStorage + IDB clear (last resort)
 *
 * Recovery priority: localStorage → IndexedDB → cookie → regenerate
 * Design: src/modules/devices/infrastructure/identity/ (design.md §3)
 * 
 * IMPORTANT: The IdUnique is scoped to the instance (idInstance) to ensure
 * that devices enrolled for one account/instance are not reused when logging
 * into a different account/instance on the same browser.
 */
import type { AppDatabase } from '@/shared/db/database';

const KEY_PREFIX = 'mi_id_unique';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365 * 10; // 10 years in seconds

export class DeviceIdentityService {
  /** In-memory cache for the current session. */
  private cached: Map<number, string> = new Map();

  constructor(private readonly db: AppDatabase) {}

  // ── Public API ──────────────────────────────────────────────────────────

  /**
   * Gets or creates a stable device ID scoped to the given instance.
   * 
   * @param idInstance - The instance ID to scope the device ID to. When provided,
   *                     each instance gets its own device ID. When omitted, falls
   *                     back to a global device ID (legacy behavior).
   */
  async getOrCreateIdUnique(idInstance?: number): Promise<string> {
    const cacheKey = idInstance ?? -1;
    const cachedValue = this.cached.get(cacheKey);
    if (cachedValue !== undefined) return cachedValue;

    const key = getStorageKey(idInstance);
    const recovered = await recoverFromAnyLayer(key, this.db);
    if (recovered !== null) {
      await writeAllLayers(key, recovered, this.db);
      this.cached.set(cacheKey, recovered);
      return recovered;
    }

    const id = await deriveNewId(idInstance);
    await writeAllLayers(key, id, this.db);
    this.cached.set(cacheKey, id);
    return id;
  }
}

// ── Internal helpers (pure functions where possible) ────────────────────────

/**
 * Returns the storage key for the given instance.
 * When idInstance is provided, returns 'mi_id_unique_<idInstance>'.
 * When omitted, returns 'mi_id_unique' (legacy global key).
 */
function getStorageKey(idInstance?: number): string {
  return idInstance !== undefined ? `${KEY_PREFIX}_${idInstance}` : KEY_PREFIX;
}

async function deriveNewId(idInstance?: number): Promise<string> {
  const uuid = crypto.randomUUID();
  const fp = await computeFingerprint();
  const instanceSuffix = idInstance !== undefined ? `::instance:${idInstance}` : '';
  return sha256Hex(`${uuid}::${fp}${instanceSuffix}`);
}

async function recoverFromAnyLayer(key: string, db: AppDatabase): Promise<string | null> {
  // Priority 1: localStorage (fastest)
  const fromLS = localStorage.getItem(key);
  if (fromLS !== null && fromLS.length > 0) return fromLS;

  // Priority 2: IndexedDB
  try {
    if (!db.isOpen()) await db.open();
    const row = await db.settings.get(key);
    if (row !== undefined && row.value.length > 0) return row.value;
  } catch {
    // IDB unavailable — continue to cookie
  }

  // Priority 3: Cookie
  const fromCookie = readCookie(key);
  if (fromCookie !== null && fromCookie.length > 0) return fromCookie;

  return null;
}

async function writeAllLayers(key: string, id: string, db: AppDatabase): Promise<void> {
  // 1. localStorage
  localStorage.setItem(key, id);

  // 2. IndexedDB
  try {
    if (!db.isOpen()) await db.open();
    await db.settings.put({ key, value: id, updated_at: Date.now() });
  } catch {
    // IDB write failure is non-fatal — localStorage + cookie still cover identity
  }

  // 3. Cookie
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${key}=${id}; Path=/; Max-Age=${COOKIE_MAX_AGE}; SameSite=Strict${secure}`;
}

// ── Browser fingerprint ─────────────────────────────────────────────────────

async function computeFingerprint(): Promise<string> {
  const parts = {
    ua: navigator.userAgent,
    sw: screen.width,
    sh: screen.height,
    sd: screen.colorDepth,
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
    lang: navigator.language,
    cores: navigator.hardwareConcurrency,
    plat: navigator.platform,
    canvas: canvasHash(),
    webgl: webglHash(),
  };
  return sha256Hex(JSON.stringify(parts));
}

function canvasHash(): string {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return 'no-canvas';
    ctx.fillStyle = '#f60';
    ctx.font = '14px Arial';
    ctx.fillText('myintelli-clock-fp', 2, 15);
    return canvas.toDataURL();
  } catch {
    return 'canvas-error';
  }
}

function webglHash(): string {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') as WebGLRenderingContext | null;
    if (!gl) return 'no-webgl';
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    if (!ext) return 'no-ext';
    const vendor = gl.getParameter(ext.UNMASKED_VENDOR_WEBGL) as string;
    const renderer = gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) as string;
    return `${vendor}::${renderer}`;
  } catch {
    return 'webgl-error';
  }
}

// ── SHA-256 ─────────────────────────────────────────────────────────────────

export async function sha256Hex(input: string): Promise<string> {
  const encoded = new TextEncoder().encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoded);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

// ── Cookie utilities ─────────────────────────────────────────────────────────

function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const trimmed = cookie.trim();
    if (trimmed.startsWith(prefix)) {
      return trimmed.slice(prefix.length);
    }
  }
  return null;
}
